/*
CRITICAL FREEZE NOTICE — /products (UnifiedProductsPage)
Do NOT modify this page (content, header, SEO, layout) without explicit owner permission.
Owner directive (2025-09-01): Keep title/description static. Filters may only change displayed products.
Any change must start with: "EXPLICIT PERMISSION: PRODUCTS".
*/
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useLocation, Link, useNavigate } from "react-router-dom";
import BohoNav from "@/components/BohoNav";
import BohoFooter from "@/components/BohoFooter";
import SEO from "@/components/SEO";

import RecentlyViewed from "@/components/RecentlyViewed";
import { ProductRecommendationEngine } from "@/components/ProductRecommendationEngine";
import { EnhancedShopifyProductCard } from "@/components/enhanced";
import { useShopifyQuery } from "@/hooks/useShopifyQuery";
import { ShopifyVariant, ShopifyProduct } from "@/hooks/useShopify";
import { useSelectiveShopify } from "@/hooks/useSelectiveShopify";
import { useProducts, ProductWithCategories } from "@/hooks/useProducts";
import { AnimatedText } from "@/components/AnimatedText";
import { Eye, ChevronDown } from "lucide-react";
import { useFuzzySearch } from '@/hooks/useFuzzySearch';
import { Button } from "@/components/ui/button";
import { useStandardQuickView } from '@/hooks/useStandardQuickView';
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, Grid3X3, Grid2X2, LayoutGrid, Maximize, Minimize, Loader2, Play, Pause } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { focusElementById } from "@/utils/focusManagement";
import { productFocusManager } from "@/utils/productFocusManager";

import ProductFilters from "@/components/ProductFilters";
import RevisionBadge from "@/components/RevisionBadge";
import CollapsibleTopsFilter from "@/components/CollapsibleTopsFilter";
import CollapsibleDressesFilter from "@/components/CollapsibleDressesFilter";
import CollapsibleBottomsFilter from "@/components/CollapsibleBottomsFilter";
import CollapsibleJewelryFilter from "@/components/CollapsibleJewelryFilter";
import { searchAnalytics } from '@/utils/searchAnalytics';
import { productionLogger } from '@/utils/production-logger';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sparkles, Star } from "lucide-react";
import { getProductSizeGroups, getClothingSizeGroups } from "@/utils/sizeGroups";
import { ProductCardSkeleton } from "@/components/LoadingSystem";

interface PriceRange {
  min: number;
  max: number;
}

const UnifiedProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(searchParams.get('category') || undefined);
  const [sortBy, setSortBy] = useState<'title' | 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated'>(() => {
    const sortParam = searchParams.get('sort');
    const validSorts = ['title', 'price_asc', 'price_desc', 'newest', 'oldest', 'updated'] as const;
    return validSorts.includes(sortParam as any) ? sortParam as typeof validSorts[number] : 'updated';
  });
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filteredProductsCount, setFilteredProductsCount] = useState(0);
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  
  // Advanced filter states - initialize from URL
  const [priceRange, setPriceRange] = useState<[number, number]>(() => {
    const minPrice = parseInt(searchParams.get('minPrice') || '0');
    const maxPrice = parseInt(searchParams.get('maxPrice') || '500');
    return [minPrice, maxPrice];
  });
  const [selectedSizes, setSelectedSizes] = useState<string[]>(() => {
    const sizes = searchParams.get('sizes');
    return sizes ? sizes.split(',').filter(Boolean) : [];
  });
  const [selectedColors, setSelectedColors] = useState<string[]>(() => {
    const colors = searchParams.get('colors');
    return colors ? colors.split(',').filter(Boolean) : [];
  });
  const [hideOutOfStock, setHideOutOfStock] = useState<boolean>(() => {
    return searchParams.get('hideOutOfStock') === 'true';
  });

  // Tops sub-category states
  const [selectedTopsSubCategories, setSelectedTopsSubCategories] = useState<string[]>(() => {
    const subCats = searchParams.get('topsSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showTopsSubCategories, setShowTopsSubCategories] = useState(() => {
    return selectedCategory === 'tops';
  });
  
  // Dresses sub-category states
  const [selectedDressesSubCategories, setSelectedDressesSubCategories] = useState<string[]>(() => {
    const subCats = searchParams.get('dressesSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showDressesSubCategories, setShowDressesSubCategories] = useState(() => {
    return selectedCategory === 'dresses';
  });
  
  // Bottoms sub-category states
  const [selectedBottomsSubCategories, setSelectedBottomsSubCategories] = useState<string[]>(() => {
    const subCats = searchParams.get('bottomsSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showBottomsSubCategories, setShowBottomsSubCategories] = useState(() => {
    return selectedCategory === 'bottoms';
  });
  
  // Jewelry sub-category states
  const [selectedJewelrySubCategories, setSelectedJewelrySubCategories] = useState<string[]>(() => {
    const subCats = searchParams.get('jewelrySubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showJewelrySubCategories, setShowJewelrySubCategories] = useState(() => {
    return selectedCategory === 'jewelry';
  });
  
  // Display toggle states - now managed by useUserPreferences hook
  const { viewSize, columnCount, setViewSize, setColumnCount } = useUserPreferences();
  
  // Progressive loading states
  const [progressiveProducts, setProgressiveProducts] = useState<ShopifyProduct[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [currentLoadPage, setCurrentLoadPage] = useState(1);
  const { handleQuickView } = useStandardQuickView();
  
  const skipNextUrlSync = useRef(false);
  const location = useLocation();
  const suppressScrollOnPageChange = useRef(false);

  // Tops sub-categories configuration
  const topsSubCategories = [
    'Blouses', 'Camis', 'Cardigans', 'Hoodies', 
    'Jackets', 'Kimonos', 'Sweaters', 'T-Shirts', 'Tanks'
  ];
  
  // Dresses sub-categories configuration
  const dressesSubCategories = [
    'Mini Dresses'
  ];

  // Jewelry sub-categories configuration  
  const jewelrySubCategories = [
    'Bracelets', 'Earrings', 'Necklaces', 'Jewelry Sets'
  ];

  // Hero visibility control from Account toggle (only for /products page)
  const [heroElementsVisible, setHeroElementsVisible] = useState(() => {
    return localStorage.getItem('heroAnimationsEnabled') !== 'false'; // Default to true (show elements)
  });

  // Listen for localStorage changes to update visibility
  useEffect(() => {
    const handleStorageChange = () => {
      setHeroElementsVisible(localStorage.getItem('heroAnimationsEnabled') !== 'false');
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check on component mount in case it changed
    const currentSetting = localStorage.getItem('heroAnimationsEnabled');
    setHeroElementsVisible(currentSetting !== 'false');
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);


  // Hooks with error boundary
  const { products: shopifyProducts, collections, loading: shopifyLoading, searchProducts, clearCacheAndRefresh, silentRefresh } = useShopifyQuery();
  const { loadProducts, clearCache: clearSelectiveCache } = useSelectiveShopify();
  const { products: databaseProducts, loading: databaseLoading } = useProducts();
  
  // Combine both data sources
  const products = useMemo(() => {
    const combined = [...shopifyProducts];
    
    // Convert database products to match Shopify format for compatibility
    databaseProducts.forEach((dbProduct) => {
      const shopifyFormatProduct: ShopifyProduct = {
        id: `db_${dbProduct.id}`,
        title: dbProduct.name,
        handle: dbProduct.slug,
        description: dbProduct.description || '',
        productType: dbProduct.categories?.[0]?.name || 'General',
        tags: dbProduct.tags,
        vendor: 'Boho Style Co.',
        createdAt: dbProduct.created_at,
        updatedAt: dbProduct.updated_at,
        totalInventory: dbProduct.inventory_quantity,
        availableForSale: dbProduct.is_active && dbProduct.inventory_quantity > 0,
        images: dbProduct.images.map((url, index) => ({
          id: `${dbProduct.id}_img_${index}`,
          url: url,
          altText: dbProduct.name,
        })),
        variants: [{
          id: `${dbProduct.id}_variant`,
          title: 'Default',
          price: {
            amount: dbProduct.price.toString(),
            currencyCode: 'USD'
          },
          compareAtPrice: dbProduct.compare_price ? {
            amount: dbProduct.compare_price.toString(),
            currencyCode: 'USD'
          } : null,
          availableForSale: dbProduct.is_active && dbProduct.inventory_quantity > 0,
          quantityAvailable: dbProduct.inventory_quantity,
          selectedOptions: []
        }],
        options: [],
        priceRange: {
          minVariantPrice: {
            amount: dbProduct.price.toString(),
            currencyCode: 'USD'
          },
          maxVariantPrice: {
            amount: dbProduct.price.toString(),
            currencyCode: 'USD'
          }
        }
      };
      combined.push(shopifyFormatProduct);
    });
    
    return combined;
  }, [shopifyProducts, databaseProducts]);
  
  // Create local filtering function that works on combined products
  const searchCombinedProducts = useCallback((query: string, categoryHandle?: string, sortOption?: string) => {
    let filtered = [...products];
    
    // Filter by search query
    if (query.trim()) {
      const searchTerm = query.toLowerCase();
      filtered = filtered.filter(product => 
        product.title.toLowerCase().includes(searchTerm) ||
        product.description.toLowerCase().includes(searchTerm) ||
        product.productType.toLowerCase().includes(searchTerm) ||
        product.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }
    
    // Filter by category/collection
    if (categoryHandle) {
      // For Shopify products, check if they belong to the collection
      const collection = collections.find(c => c.handle === categoryHandle);
      if (collection) {
        filtered = filtered.filter(product => {
          // Shopify products: check if in collection OR has matching tags
          if (!product.id.startsWith('db_')) {
            // Check if explicitly in collection
            const inCollection = collection.productHandles?.includes(product.handle) || false;
            
            // Also check if product has tags that match the category
            const categoryLower = categoryHandle.toLowerCase();
            const hasMatchingTag = product.tags.some(tag => {
              const tagLower = tag.toLowerCase();
              return tagLower === categoryLower || 
                     (categoryLower === 'tops' && ['blouses', 'shirts', 'sweaters', 'cardigans', 'tunics'].includes(tagLower)) ||
                     (categoryLower === 'bottoms' && ['pants', 'jeans', 'shorts', 'skirts'].includes(tagLower)) ||
                     (categoryLower === 'dresses' && tagLower === 'dress') ||
                     (categoryLower === 'jewelry' && ['necklaces', 'earrings', 'bracelets', 'rings'].includes(tagLower)) ||
                     (categoryLower === 'accessories' && ['bags', 'scarves', 'hats', 'belts'].includes(tagLower));
            });
            
            return inCollection || hasMatchingTag;
          }
          // Database products: check productType and tags
          else {
            const categoryLower = categoryHandle.toLowerCase();
            const productTypeLower = product.productType.toLowerCase();
            const hasMatchingTag = product.tags.some(tag => tag.toLowerCase().includes(categoryLower));
            
            // Map common category names
            const categoryMappings = {
              'tops': ['tops', 'blouses', 'shirts', 'sweaters', 'cardigans', 'tunics'],
              'bottoms': ['bottoms', 'pants', 'jeans', 'shorts', 'skirts'],
              'dresses': ['dresses', 'dress'],
              'jewelry': ['jewelry', 'necklaces', 'earrings', 'bracelets', 'rings'],
              'accessories': ['accessories', 'bags', 'scarves', 'hats', 'belts']
            };
            
            const allowedTypes = categoryMappings[categoryLower] || [categoryLower];
            const hasMatchingType = allowedTypes.some(type => 
              productTypeLower.includes(type) || hasMatchingTag
            );
            
            return hasMatchingType;
          }
        });
      }
    }
    
    // Sort products
    if (sortOption) {
      switch (sortOption) {
        case 'title':
          filtered.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'price_asc':
          filtered.sort((a, b) => {
            const priceA = parseFloat(a.variants[0]?.price?.amount || '0');
            const priceB = parseFloat(b.variants[0]?.price?.amount || '0');
            return priceA - priceB;
          });
          break;
        case 'price_desc':
          filtered.sort((a, b) => {
            const priceA = parseFloat(a.variants[0]?.price?.amount || '0');
            const priceB = parseFloat(b.variants[0]?.price?.amount || '0');
            return priceB - priceA;
          });
          break;
        case 'newest':
          filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        case 'oldest':
          filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          break;
        case 'updated':
        default:
          filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
          break;
      }
    }
    
    return filtered;
  }, [products, collections]);
  
  // Keep curating message until images actually load
  const [showCurating, setShowCurating] = useState(true);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const productsLoading = shopifyLoading || databaseLoading || (showCurating && !hasInitialLoad);
  const navigate = useNavigate();
  
  // Only hide curating after products load AND sufficient time for images
  useEffect(() => {
    if (products.length > 0 && !shopifyLoading && !databaseLoading) {
      setHasInitialLoad(true);
      // Quick transition with skeleton cards providing visual feedback
      const timer = setTimeout(() => {
        setShowCurating(false);
      }, 100); // Fast display with skeleton cards handling load states
      
      return () => clearTimeout(timer);
    }
  }, [products.length, shopifyLoading, databaseLoading]);
  
  // Reset when navigating back to page
  useEffect(() => {
    setShowCurating(true);
    setHasInitialLoad(false);
  }, []);
  
  // Fuzzy search integration with surgical precision for fashion terms
  const fuzzySearchOptions = useMemo(() => ({
    threshold: 0.2, // More strict threshold to prevent midi/maxi confusion
    keys: [
      { name: 'title', weight: 0.4 },
      { name: 'description', weight: 0.2 },
      { name: 'productType', weight: 0.3 },
      { name: 'tags', weight: 0.1 }
    ],
    // Add exact word matching for precise fashion terms
    exactWordMatch: ['midi', 'maxi', 'mini', 'crop', 'high-waisted', 'low-rise']
  }), []);

  const {
    search: fuzzySearch,
    searchResults,
    clearSearch: clearFuzzySearch,
    getFilteredProducts: getFuzzyFilteredProducts
  } = useFuzzySearch(products, fuzzySearchOptions);
  
  // Check if a collection is empty
  const isCollectionEmpty = (collectionHandle: string) => {
    const collectionProducts = searchProducts('', collectionHandle, 'newest');
    return collectionProducts.length === 0;
  };
  
  // Filter out empty collections for the filter component
  const nonEmptyCollections = collections.filter(collection => !isCollectionEmpty(collection.handle));
  
  // Safety: if body scroll was locked by a previous dropdown, restore it on mount
  useEffect(() => {
    const body = document.body as HTMLBodyElement & { style: CSSStyleDeclaration & { overflowAnchor?: string } };
    const top = Math.abs(parseInt(body.style.top || '0')) || 0;
    if (body.style.position === 'fixed') {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.left = '';
      body.style.right = '';
      body.style.overflow = '';
      window.scrollTo(0, top);
    }
  }, []);

  // Scroll position restoration for back navigation
  useEffect(() => {
    const saveScrollPosition = () => {
      sessionStorage.setItem('productsPageScrollPosition', window.scrollY.toString());
    };

    // Save scroll position when navigating away
    const handleBeforeUnload = () => {
      saveScrollPosition();
      sessionStorage.setItem('productsPageNumber', currentPage.toString());
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveScrollPosition();
        sessionStorage.setItem('productsPageNumber', currentPage.toString());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Save scroll position and page when component unmounts (navigation away)
      saveScrollPosition();
      sessionStorage.setItem('productsPageNumber', currentPage.toString());
    };
  }, [currentPage]);

  // Restore scroll position ONLY when returning via back button from product page
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('productsPageScrollPosition');
    const savedPage = sessionStorage.getItem('productsPageNumber');
    const isBackNavigation = sessionStorage.getItem('isBackNavigation');
    
    // Only restore scroll position if this was a back navigation from a product page
    if (savedScrollPosition && !productsLoading && products.length > 0 && isBackNavigation === 'true') {
      const scrollY = parseInt(savedScrollPosition, 10);
      const pageNum = parseInt(savedPage || '1', 10);
      
      // Restore page number first
      if (pageNum > 1 && pageNum !== currentPage) {
        setCurrentPage(pageNum);
      }
      
      // Wait longer for products to fully render
      setTimeout(() => {
        window.scrollTo(0, scrollY);
        // Clear the saved position after successful restore
        sessionStorage.removeItem('productsPageScrollPosition');
        sessionStorage.removeItem('productsPageNumber');
        sessionStorage.removeItem('isBackNavigation');
      }, 500);
    } else if (!productsLoading && products.length > 0 && isBackNavigation !== 'true') {
      // Only clear if products are loaded AND it's NOT a back navigation
      // This prevents clearing the flag before products finish loading
      sessionStorage.removeItem('productsPageScrollPosition');
      sessionStorage.removeItem('productsPageNumber');
      sessionStorage.removeItem('isBackNavigation');
    }
  }, [productsLoading, products.length, currentPage]);

  // Restore scroll position after refresh (only for refresh, not navigation)
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('refreshScrollPosition');
    const isRefresh = performance.navigation?.type === 1; // Navigation type 1 = reload
    
    if (savedScrollPosition && isRefresh) {
      const scrollY = parseInt(savedScrollPosition, 10);
      // Wait for page to fully load before scrolling  
      setTimeout(() => {
        window.scrollTo(0, scrollY);
        sessionStorage.removeItem('refreshScrollPosition');
      }, 100);
    } else {
      // Clear stale refresh position data
      sessionStorage.removeItem('refreshScrollPosition');
    }
  }, []);

  // Collection data now comes from Shopify only - no static data

  // Resolve known handle aliases to actual Shopify handles
  const resolveCollectionHandle = (handle?: string) => {
    if (!handle) return undefined;
    if (collections.some(c => c.handle === handle)) return handle;
    const aliases: Record<string, string[]> = {
      'bath-body': ['bath-and-body'],
      'home-decor': ['decor'],
      'decor': ['home-decor'],
    };
    const candidates = aliases[handle] || [];
    const match = candidates.find(h => collections.some(c => c.handle === h));
    return match || handle;
  };

  const resolvedCategory = resolveCollectionHandle(selectedCategory);

  // Get collection data from Shopify only
  const selectedCollectionData = resolvedCategory 
    ? collections.find(c => c.handle === resolvedCategory)
    : null;

  // Trigger fuzzy search when search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      fuzzySearch(searchQuery);
    } else {
      clearFuzzySearch();
    }
  }, [searchQuery, fuzzySearch, clearFuzzySearch]);

  // Memoize expensive filtering operations
  const allFilteredProducts = useMemo(() => {
    
    // Use fuzzy search results if there's a search query, otherwise use all products or collection filter
    let baseFilteredProducts;
    if (searchQuery.trim()) {
      // Use fuzzy search results
      baseFilteredProducts = searchResults.map(result => result.item);
    } else {
      // No search query - use collection filtering or all products with sorting
      if (selectedCategory === 'sale') {
        // For sale, start with all products and let the sale filter handle it
        baseFilteredProducts = searchCombinedProducts('', undefined, sortBy);
      } else {
        baseFilteredProducts = resolvedCategory 
          ? searchCombinedProducts('', resolvedCategory, sortBy)
          : searchCombinedProducts('', undefined, sortBy);
      }
    }
    
    // Special handling for Tops collection with sub-categories
    if (selectedCategory === 'tops' && selectedTopsSubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        return selectedTopsSubCategories.some(subCategory => 
          product.tags?.some(tag => {
            const tagLower = tag.toLowerCase();
            const subCategoryLower = subCategory.toLowerCase();
            
            // More flexible matching for common tag variations
            if (subCategoryLower === 'blouses') {
              return tagLower === 'blouses' || tagLower === 'blouse';
            }
            
            return tagLower === subCategoryLower;
          })
        );
      });
    }
    
    // Special handling for Dresses collection with sub-categories
    if (selectedCategory === 'dresses' && selectedDressesSubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        return selectedDressesSubCategories.some(subCategory => 
          product.tags?.some(tag => {
            const tagLower = tag.toLowerCase();
            const subCategoryLower = subCategory.toLowerCase();
            
            // Extract the base type (maxi, midi, mini) from subcategory
            const baseType = subCategoryLower.replace(' dresses', '').replace(' dress', '');
            
            // Match various tag formats:
            // "maxi", "maxi dress", "maxi dresses", "Maxi Dress", "Maxi Dresses"
            return tagLower === baseType || 
                   tagLower === `${baseType} dress` || 
                   tagLower === `${baseType} dresses` ||
                   tagLower.includes(baseType);
          })
        );
      });
    }
    
    // Special handling for Bottoms collection with sub-categories
    if (selectedCategory === 'bottoms' && selectedBottomsSubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        return selectedBottomsSubCategories.some(subCategory => 
          product.tags?.some(tag => {
            const tagLower = tag.toLowerCase();
            const subCategoryLower = subCategory.toLowerCase();
            
            // Custom tag matching rules for bottoms subcategories
            switch (subCategoryLower) {
              case 'capris':
                return tagLower === 'capris';
              case 'flare':
                return tagLower === 'flare' || tagLower === 'flare jeans';
              case 'jeans':
                return tagLower === 'wide leg jeans' || tagLower === 'jeans' || tagLower === 'vintage denim' || tagLower === 'jorts';
              case 'joggers':
                return tagLower === 'joggers';
              case 'leggings':
                return tagLower === 'leggings';
              case 'maxi skirts':
                return tagLower === 'maxi skirts';
              case 'midi skirts':
                return tagLower === 'midi skirts';
              case 'mini skirts':
                return tagLower === 'mini skirts';
              case 'palazzo':
                return tagLower === 'palazzo';
              case 'skorts':
                return tagLower === 'skorts';
              case 'shorts':
                return tagLower === 'skorts' || tagLower === 'shorts';
              case 'wide leg':
                return tagLower === 'wide leg pants';
              default:
                return false;
            }
          })
        );
      });
    }
    
    // Special handling for Jewelry collection with sub-categories
    if (selectedCategory === 'jewelry' && selectedJewelrySubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        return selectedJewelrySubCategories.some(subCategory => 
          product.tags?.some(tag => {
            const tagLower = tag.toLowerCase();
            const subCategoryLower = subCategory.toLowerCase();
            
            // Custom tag matching rules for jewelry subcategories
            switch (subCategoryLower) {
              case 'bracelets':
                return tagLower === 'bracelets';
              case 'earrings':
                return tagLower === 'earrings';
              case 'necklaces':
                return tagLower === 'necklaces';
              case 'jewelry sets':
                return tagLower === 'jewelry sets' || tagLower === 'necklace sets' || tagLower === 'bracelet sets' || tagLower === 'earring sets';
              default:
                return false;
            }
          })
        );
      });
    }
    
    // Special handling for Sale collection - filter products with compareAtPrice > price
    if (selectedCategory === 'sale') {
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        // Use the EXACT same logic as SaleCollection.tsx
        const hasVariantsOnSale = product.variants?.some(variant => {
          const comparePrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : 0;
          const regularPrice = parseFloat(variant.price.amount);
          return comparePrice > regularPrice;
        });
        
        // Also check the main price range for sale indicators
        const mainComparePrice = product.compareAtPriceRange?.minVariantPrice ? 
          parseFloat(product.compareAtPriceRange.minVariantPrice.amount) : 0;
        const mainPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || '0');
        const hasMainSale = mainComparePrice > mainPrice;
        
        return hasVariantsOnSale || hasMainSale;
      });
    }
    
    // Apply additional filters (price, size, color, availability) after basic search/sort
    return baseFilteredProducts.map((product) => {
      let matchedVariant: ShopifyVariant | null = null;
      
      // Price filtering
      const productMinPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || '0');
      const productMaxPrice = parseFloat(product.priceRange?.maxVariantPrice?.amount || '0');
      if (priceRange[0] > 0 || priceRange[1] < 500) {
        if (productMinPrice < priceRange[0] || (priceRange[1] < 500 && productMaxPrice > priceRange[1])) {
          return null;
        }
      }
      
      // Size filtering with size groups - also track which variant matches
      if (selectedSizes.length > 0) {
        let hasMatchingSize = false;
        
        // First check if any variant has the exact size
        if (!matchedVariant) {
          selectedSizes.forEach(selectedSize => {
            if (hasMatchingSize || matchedVariant) return;
            
            product.variants?.forEach(variant => {
              if (hasMatchingSize || matchedVariant) return;
              
              const variantMatches = variant.selectedOptions?.some(option => 
                (option.name?.toLowerCase().includes('size') || option.name?.toLowerCase().includes('length')) && 
                option.value?.toLowerCase().includes(selectedSize.toLowerCase())
              );
              
              if (variantMatches) {
                matchedVariant = variant;
                hasMatchingSize = true;
              }
            });
          });
        }
        
        // If no exact variant match, check size groups
        if (!hasMatchingSize) {
          hasMatchingSize = selectedSizes.some(selectedSizeGroup => {
            return getProductSizeGroups(product).includes(selectedSizeGroup);
          });
        }
        
        if (!hasMatchingSize) return null;
      }
      
      // Color filtering - track which variant matches
      if (selectedColors.length > 0) {
        let hasMatchedColor = false;
        
        selectedColors.forEach(selectedColor => {
          if (hasMatchedColor) return;
          
          product.variants?.forEach(variant => {
            if (hasMatchedColor) return;
            
            const variantMatches = variant.selectedOptions?.some(option => 
              option.name?.toLowerCase().includes('color') && 
              option.value?.toLowerCase().includes(selectedColor.toLowerCase())
            );
            
            if (variantMatches) {
              matchedVariant = variant;
              hasMatchedColor = true;
            }
          });
          
          if (!hasMatchedColor) {
            const hasTagColor = product.tags?.some(tag => 
              tag.toLowerCase().includes(selectedColor.toLowerCase())
            ) || false;
            
            if (hasTagColor) hasMatchedColor = true;
          }
        });
        
        if (!hasMatchedColor) return null;
      }
      
      // Price-based variant matching for sorting
      if (!matchedVariant && sortBy && (sortBy === 'price_asc' || sortBy === 'price_desc')) {
        if (product.variants && product.variants.length > 0) {
          // Find the variant that matches the sort criteria
          let targetVariant = product.variants[0];
          
          product.variants.forEach(variant => {
            const currentPrice = parseFloat(variant.price.amount);
            const targetPrice = parseFloat(targetVariant.price.amount);
            
            if (sortBy === 'price_desc' && currentPrice > targetPrice) {
              targetVariant = variant;
            } else if (sortBy === 'price_asc' && currentPrice < targetPrice) {
              targetVariant = variant;
            }
          });
          
          matchedVariant = targetVariant;
        }
      }
      
      // Out of stock filtering and variant selection
      if (hideOutOfStock) {
        // Find first in-stock variant for this product
        const inStockVariant = product.variants?.find(variant => 
          variant.availableForSale && (variant.quantityAvailable || 0) > 0
        );
        
        // If no in-stock variants exist, filter out the product entirely
        if (!inStockVariant) return null;
        
        // Use the in-stock variant as the matched variant (so its image shows)
        if (!matchedVariant) {
          matchedVariant = inStockVariant;
        }
      }
      
      // Return enhanced product with matched variant info
      return {
        ...product,
        _matchedVariant: matchedVariant
      };
    }).filter(Boolean);
  }, [products, progressiveProducts, searchQuery, searchResults, resolvedCategory, sortBy, priceRange, selectedSizes, selectedColors, selectedTopsSubCategories, selectedDressesSubCategories, selectedBottomsSubCategories, selectedJewelrySubCategories, hideOutOfStock]);
  
  // Pagination constants
  const PRODUCTS_PER_PAGE = 24;
  const totalPages = Math.ceil(allFilteredProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const endIndex = startIndex + PRODUCTS_PER_PAGE;
  const filteredProducts = allFilteredProducts.slice(startIndex, endIndex);
  
  // Reset loaded image count when products change
  useEffect(() => {
    setLoadedImageCount(0);
  }, [filteredProducts.length]);

  // Progressive loading function
  const loadMoreProducts = async () => {
    if (loadingMore || !hasNextPage) return;
    
    setLoadingMore(true);
    try {
      const result = await loadProducts(currentLoadPage + 1);
      if (result && result.products && result.products.length > 0) {
        setProgressiveProducts(prev => [...prev, ...result.products]);
        setCurrentLoadPage(prev => prev + 1);
        setHasNextPage(result.hasNextPage);
      } else {
        setHasNextPage(false);
      }
    } catch (error) {
      productionLogger.error('Error loading more products:', error);
    }
    setLoadingMore(false);
  };

  // Update state when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextQ = params.get('q') || '';
    const nextCategory = params.get('category') || undefined;
    const sortParam = params.get('sort');
    const validSorts = ['title', 'price_asc', 'price_desc', 'newest', 'oldest', 'updated'] as const;
    const nextSort = validSorts.includes(sortParam as any) ? sortParam as typeof validSorts[number] : 'updated';
    const nextPage = parseInt(params.get('page') || '1');
    
    
    const nextMinPrice = parseInt(params.get('minPrice') || '0');
    const nextMaxPrice = parseInt(params.get('maxPrice') || '500');
    const nextPriceRange: [number, number] = [nextMinPrice, nextMaxPrice];
    const nextSizes = params.get('sizes') ? params.get('sizes')!.split(',').filter(Boolean) : [];
    const nextColors = params.get('colors') ? params.get('colors')!.split(',').filter(Boolean) : [];
    const nextHideOutOfStock = params.get('hideOutOfStock') === 'true';

    const basicChanged = nextQ !== searchQuery || nextCategory !== selectedCategory || 
                        nextSort !== sortBy || nextPage !== currentPage;
    const advancedChanged = nextPriceRange[0] !== priceRange[0] || nextPriceRange[1] !== priceRange[1] || 
      JSON.stringify(nextSizes) !== JSON.stringify(selectedSizes) || JSON.stringify(nextColors) !== JSON.stringify(selectedColors) ||
      nextHideOutOfStock !== hideOutOfStock;
    
    if (basicChanged || advancedChanged) {
      skipNextUrlSync.current = true;
      setSearchQuery(nextQ);
      setSelectedCategory(nextCategory);
      setSortBy(nextSort);
      setCurrentPage(nextPage);
      
      setPriceRange(nextPriceRange);
      setSelectedSizes(nextSizes);
      setSelectedColors(nextColors);
      setHideOutOfStock(nextHideOutOfStock);
    }
  }, [location.search]);

  // Update URL when filters change
  useEffect(() => {
    if (skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (sortBy !== 'updated') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    
    if (priceRange[0] > 0) params.set('minPrice', priceRange[0].toString());
    if (priceRange[1] < 500) params.set('maxPrice', priceRange[1].toString());
    if (selectedSizes.length > 0) params.set('sizes', selectedSizes.join(','));
    if (selectedColors.length > 0) params.set('colors', selectedColors.join(','));
    if (selectedTopsSubCategories.length > 0) params.set('topsSubCategories', selectedTopsSubCategories.join(','));
    if (selectedDressesSubCategories.length > 0) params.set('dressesSubCategories', selectedDressesSubCategories.join(','));
    if (selectedBottomsSubCategories.length > 0) params.set('bottomsSubCategories', selectedBottomsSubCategories.join(','));
    if (selectedJewelrySubCategories.length > 0) params.set('jewelrySubCategories', selectedJewelrySubCategories.join(','));
    if (hideOutOfStock) params.set('hideOutOfStock', 'true');

    const newString = params.toString();
    const currentString = window.location.search.replace(/^\?/, '');
    
    if (newString !== currentString) {
      setSearchParams(params, { replace: true });
    }
  }, [searchQuery, selectedCategory, sortBy, currentPage, priceRange, selectedSizes, selectedColors, selectedTopsSubCategories, selectedDressesSubCategories, selectedBottomsSubCategories, selectedJewelrySubCategories, hideOutOfStock, setSearchParams]);

  // Handle tops sub-categories expansion
  useEffect(() => {
    setShowTopsSubCategories(selectedCategory === 'tops');
    // Reset sub-categories when switching away from tops
    if (selectedCategory !== 'tops' && selectedTopsSubCategories.length > 0) {
      setSelectedTopsSubCategories([]);
    }
  }, [selectedCategory]);
  
  // Handle dresses sub-categories expansion
  useEffect(() => {
    setShowDressesSubCategories(selectedCategory === 'dresses');
    // Reset sub-categories when switching away from dresses
    if (selectedCategory !== 'dresses' && selectedDressesSubCategories.length > 0) {
      setSelectedDressesSubCategories([]);
    }
  }, [selectedCategory]);
  
  // Handle bottoms sub-categories expansion
  useEffect(() => {
    setShowBottomsSubCategories(selectedCategory === 'bottoms');
    // Reset sub-categories when switching away from bottoms
    if (selectedCategory !== 'bottoms' && selectedBottomsSubCategories.length > 0) {
      setSelectedBottomsSubCategories([]);
    }
  }, [selectedCategory]);
  
  // Handle jewelry sub-categories expansion
  useEffect(() => {
    setShowJewelrySubCategories(selectedCategory === 'jewelry');
    // Reset sub-categories when switching away from jewelry
    if (selectedCategory !== 'jewelry' && selectedJewelrySubCategories.length > 0) {
      setSelectedJewelrySubCategories([]);
    }
  }, [selectedCategory]);

  // Enforce compact layout on category pages
  useEffect(() => {
    if (selectedCategory) {
      if (viewSize !== 'small') setViewSize('small');
      if (columnCount !== 4) setColumnCount(4);
    }
  }, [selectedCategory]);

  // Analytics tracking
  useEffect(() => {
    if (searchQuery) {
      searchAnalytics.trackSearch(searchQuery, allFilteredProducts.length);
      if (allFilteredProducts.length === 0) {
        searchAnalytics.trackZeroResults(searchQuery);
      }
    }
  }, [searchQuery, allFilteredProducts.length]);

  // Update filtered products count
  useEffect(() => {
    setFilteredProductsCount(allFilteredProducts.length);
  }, [allFilteredProducts.length]);

  // Reset page to 1 when category changes via any method
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Page boundary validation - reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Scroll to top when page changes via pagination
  useEffect(() => {
    if (suppressScrollOnPageChange.current) {
      suppressScrollOnPageChange.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Generate dynamic grid classes
  const getGridClasses = () => {
    if (viewSize === 'large') {
      switch (columnCount) {
        case 2: return 'grid-cols-2 md:grid-cols-2';
        case 3: return 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3';
        case 4: return 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
      }
    } else if (viewSize === 'medium') {
      switch (columnCount) {
        case 2: return 'grid-cols-2 sm:grid-cols-2';
        case 3: return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3';
        case 4: return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      }
    } else {
      // Small view - use standard responsive grid
      switch (columnCount) {
        case 2: return 'grid-cols-1 sm:grid-cols-2';
        case 3: return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3';
        case 4: return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      }
    }
  };

  const getGapClass = () => {
    // Use separate row and column gaps to prevent hover collision
    // For 2 columns, use enhanced spacing to prevent squishing
    if (columnCount === 2) {
      if (viewSize === 'small') return 'gap-x-3 gap-y-4'; // row gap increased
      if (viewSize === 'medium') return 'gap-x-4 gap-y-5'; // row gap increased
      if (viewSize === 'large') return 'gap-x-5 gap-y-6'; // row gap increased
    }
    
    // For 3+ columns, use same gap as medium/large views with extra row spacing
    return 'gap-x-2.5 gap-y-3.5';
  };

  // Clear all filters function
  const clearAllFilters = () => {
    suppressScrollOnPageChange.current = true;
    setSearchQuery('');
    clearFuzzySearch(); // Clear fuzzy search results
    setSelectedCategory(undefined);
    setSortBy('updated');
    setCurrentPage(1);
    
    setPriceRange([0, 500]);
    setSelectedSizes([]);
    setSelectedColors([]);
    setSelectedTopsSubCategories([]);
    setSelectedDressesSubCategories([]);
    setSelectedBottomsSubCategories([]);
    setSelectedJewelrySubCategories([]);
    setHideOutOfStock(false);
  };

  // Show page content immediately - no global loading screen

  return (
    <div className="min-h-screen">
      <SEO
        title="Shop Bohemian Products - Jewelry & Accessories - Boho Style Co."
        description="Browse our complete collection of bohemian fashion, bijoux femme, jewelry, and accessories. Filter by style, price, and collection to find your perfect boho pieces."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
           "name": "Bohemian Products",
           "description": "Where bohemian wanderlust meets Southern charm. Each piece in our collection is handpicked for its artisan quality and timeless appeal -- jewelry that tells stories, fashion that embraces your free spirit, and home accents that create sanctuary.",
           "url": `https://bohostyleco.com/products${selectedCategory ? `?category=${selectedCategory}` : ''}`,
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": allFilteredProducts.length,
            "itemListElement": filteredProducts.slice(0, 5).map((product, index) => ({
              "@type": "Product",
              "position": index + 1,
              "name": product.title,
              "description": product.description,
              "image": product.images?.[0]?.url,
              "url": `https://bohostyleco.com/shopify-product/${product.handle}`,
            }))
          }
        }}
      />
       
       <BohoNav />
      
      <div className="min-h-screen bg-background overflow-x-hidden">
        <div className="container mx-auto px-3 sm:px-4 pt-2 md:pt-4 pb-8 max-w-full">
          {/* Header - Title left, Controls right on same line */}
          <div className="mb-4 max-w-full">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
              {/* Title - Left aligned */}
              <h1 className="text-4xl lg:text-5xl mb-4 text-accent" style={{ 
                fontFamily: "'Dancing Script', 'Brush Script MT', cursive", 
                fontWeight: 400
              }}>
                Products
              </h1>
              
              {/* All Controls - Right aligned on desktop */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 lg:flex-row lg:items-center">
                {/* Search Box */}
                <div className="sm:max-w-md">
                  <Input
                    type="search"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setSearchQuery('');
                        clearFuzzySearch();
                      }
                    }}
                    className="w-full"
                  />
                </div>

                 {/* Desktop Controls */}
                 <div className="hidden lg:flex items-center gap-3 flex-wrap">
                <TooltipProvider>

                   {/* View Size Controls */}
                   <div className="hidden sm:flex items-center space-x-1">
                     <span className="text-xs font-medium">View Size:</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={viewSize === 'large' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setViewSize('large')}
                          className="px-1.5 h-6"
                        >
                          <Maximize className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Large - Tall Portrait Images</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={viewSize === 'medium' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setViewSize('medium')}
                          className="px-1.5 h-6"
                        >
                          <LayoutGrid className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Medium - Medium Square Images</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={viewSize === 'small' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setViewSize('small')}
                          className="px-1.5 h-6"
                        >
                          <Minimize className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Small - Tiny Square Thumbnails</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                   {/* Column Controls */}
                   <div className="hidden sm:flex items-center space-x-1">
                     <span className="text-xs font-medium">Columns:</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={columnCount === 2 ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setColumnCount(2)}
                          className="px-1.5 h-6"
                        >
                          <Grid2X2 className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>2-Column Products List</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={columnCount === 3 ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setColumnCount(3)}
                          className="px-1.5 h-6"
                        >
                          <Grid3X3 className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="end">
                        <p>3-Column Products List</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={columnCount === 4 ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setColumnCount(4)}
                          className="px-1.5 h-6"
                        >
                          <LayoutGrid className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="end">
                        <p>4-Column Products List</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                 </TooltipProvider>
              </div>
            </div>
          </div>
            
            {/* Intro Paragraph - Toggle controlled visibility */}
            {heroElementsVisible && (
              <div className="mb-4">
                <p className="text-muted-foreground text-sm leading-relaxed max-w-none line-clamp-2 md:line-clamp-4 lg:line-clamp-none">
                  Step into a world where free-spirited wanderlust meets timeless elegance, where every piece in our collection has been lovingly hand-picked for its distinctive personality and undeniable charm. These aren't just products - they're conversation starters, story makers, and soul-stirring treasures that arrive in very limited runs, ensuring your style remains authentically yours. From flowing fabrics that dance with every movement to jewelry that catches light like captured sunbeams, each item embodies that perfect balance of bohemian spirit and refined taste. Whether you're drawn to the earthy textures of natural materials, the whispered secrets held within vintage-inspired pieces, or the gentle energy of thoughtfully crafted accessories, our curated selection celebrates individuality and intentional living. Every treasure tells a tale of wanderlust and wonder, inviting you to embrace a lifestyle where beauty meets purpose, and where your choices reflect the extraordinary journey of becoming exactly who you're meant to be.
                </p>
              </div>
            )}
          </div>

          <div className="w-full min-w-0">
            <div className="flex flex-col lg:flex-row gap-4 md:gap-6 products-expand">
             {/* Sticky Filters Sidebar - Enhanced for Phase 2 */}
            <div className="w-full lg:w-[200px] flex-shrink-0 space-y-2 md:space-y-6 lg:sticky lg:top-4 lg:self-start lg:z-20">
              <div className="bg-card rounded-lg border border-border/50">
                {/* Mobile Toggle Button */}
                <button
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="lg:hidden w-full p-3 flex items-center justify-between text-left hover:bg-muted/50 rounded-t transition-colors"
                >
                  <h3 className="font-medium text-foreground text-sm">
                    Filter Products
                  </h3>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Desktop Title (Always Visible) */}
                <div className="hidden lg:block p-4 border-b border-border/30">
                  <h3 className="font-medium text-foreground text-base">
                    Filter Products
                  </h3>
                </div>
                
                {/* Filter Content - Collapsible on Mobile */}
                <div className={`${showMobileFilters ? 'block' : 'hidden'} lg:block p-3 lg:p-4`}>
                {/* All Collections - Top Level */}
                <div className="mb-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="collection"
                      checked={!selectedCategory}
                      onChange={() => {
                        setSelectedCategory(undefined);
                        setCurrentPage(1);
                      }}
                      className="w-4 h-4 accent-primary bg-background border-border focus:ring-primary focus:ring-2"
                    />
                    <span className="text-sm text-muted-foreground/50">All Collections</span>
                  </label>
                </div>

                {/* Tiny Vertical Space */}
                <div className="mb-4"></div>

                {/* Separator */}
                <div className="mb-4">
                  <div className="h-px bg-border/30"></div>
                </div>

                {/* Featured */}
                <div className="mb-4">
                  <label className="text-xs md:text-sm font-medium mb-1 md:mb-3 block">Featured</label>
                  <div className="space-y-1 md:space-y-2">
                         {collections.filter(collection => 
                          !isCollectionEmpty(collection.handle) && 
                          ['goddess', 'chrome-hearts', 'home-decor'].includes(collection.handle)
                        ).sort((a, b) => {
                          // Order: Goddess, Chrome Hearts, Home Decor, Sale
                          const order = ['goddess', 'chrome-hearts', 'home-decor'];
                          return order.indexOf(a.handle) - order.indexOf(b.handle);
                        }).map((collection, index) => (
                          <div key={collection.handle}>
                            <label className="flex items-center space-x-1.5 md:space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="collection"
                                checked={selectedCategory === collection.handle}
                                onChange={() => {
                                  setSelectedCategory(collection.handle);
                                  setCurrentPage(1);
                                }}
                                className="w-3 h-3 md:w-4 md:h-4 accent-primary bg-background border-border focus:ring-primary focus:ring-2"
                              />
                              <span className="text-xs md:text-sm text-muted-foreground/50">{collection.title}</span>
                            </label>
                             {/* Add Sale after Home Decor */}
                             {collection.handle === 'home-decor' && (
                               <label className="flex items-center space-x-1.5 md:space-x-2 cursor-pointer mt-1">
                                 <input
                                   type="radio"
                                   name="collection"
                                   checked={selectedCategory === 'sale'}
                                   onChange={() => {
                                     setSelectedCategory('sale');
                                     setCurrentPage(1);
                                   }}
                                   className="w-3 h-3 md:w-4 md:h-4 accent-primary bg-background border-border focus:ring-primary focus:ring-2"
                                 />
                                 <span className="text-xs md:text-sm flex items-center gap-1 text-accent">
                                   <Sparkles className="w-3 h-3" />
                                   Sale
                                 </span>
                               </label>
                             )}
                          </div>
                         ))}
                   </div>
                 </div>

                {/* Separator */}
                <div className="mb-2 md:mb-4">
                  <div className="h-px bg-border/30"></div>
                </div>

                {/* Product Collections */}
                <div className="mb-3 md:mb-6">
                  <label className="text-xs md:text-sm font-medium mb-1 md:mb-3 block">Product Collections</label>
                  <div className="space-y-1 md:space-y-2">
                        {collections.filter(collection => 
                          !isCollectionEmpty(collection.handle) && 
                          !['chrome-hearts', 'goddess', 'home-decor'].includes(collection.handle)
                        ).sort((a, b) => {
                          // Define desired order: Tops, Bottoms, Lounge Wear, All-in-Ones, Dresses, Shoes, Bags, Jewelry, Accessories
                          const order = ['tops', 'bottoms', 'lounge-wear', 'one-pieces', 'dresses', 'shoes', 'bags', 'jewelry', 'accessories'];
                          const indexA = order.indexOf(a.handle);
                          const indexB = order.indexOf(b.handle);
                          
                          // If both are in the order array, sort by their position
                          if (indexA !== -1 && indexB !== -1) {
                            return indexA - indexB;
                          }
                          // If only one is in the order array, prioritize it
                          if (indexA !== -1) return -1;
                          if (indexB !== -1) return 1;
                          // If neither is in the order array, sort alphabetically
                          return a.title.localeCompare(b.title);
                          }).map(collection => {
                            // Rename "One-Pieces" to "All-in-Ones" for display
                            const displayTitle = collection.handle === 'one-pieces' ? 'All-in-Ones' : collection.title;
                            return { ...collection, title: displayTitle };
                          }).map(collection =>
                            collection.handle === 'tops' ? (
                              <CollapsibleTopsFilter
                                key={collection.handle}
                                collection={collection}
                                selectedCategory={selectedCategory}
                                selectedTopsSubCategories={selectedTopsSubCategories}
                                showTopsSubCategories={showTopsSubCategories}
                                onCategoryChange={setSelectedCategory}
                                onSubCategoryChange={setSelectedTopsSubCategories}
                                onToggleExpansion={setShowTopsSubCategories}
                                onPageChange={setCurrentPage}
                              />
                            ) : collection.handle === 'dresses' ? (
                              <CollapsibleDressesFilter
                                key={collection.handle}
                                collection={collection}
                                selectedCategory={selectedCategory}
                                selectedDressesSubCategories={selectedDressesSubCategories}
                                showDressesSubCategories={showDressesSubCategories}
                                onCategoryChange={setSelectedCategory}
                                onSubCategoryChange={setSelectedDressesSubCategories}
                                onToggleExpansion={setShowDressesSubCategories}
                                onPageChange={setCurrentPage}
                              />
                            ) : collection.handle === 'bottoms' ? (
                              <CollapsibleBottomsFilter
                                key={collection.handle}
                                collection={collection}
                                selectedCategory={selectedCategory}
                                selectedBottomsSubCategories={selectedBottomsSubCategories}
                                showBottomsSubCategories={showBottomsSubCategories}
                                onCategoryChange={setSelectedCategory}
                                onSubCategoryChange={setSelectedBottomsSubCategories}
                                onToggleExpansion={setShowBottomsSubCategories}
                                onPageChange={setCurrentPage}
                              />
                            ) : collection.handle === 'jewelry' ? (
                              <CollapsibleJewelryFilter
                                key={collection.handle}
                                collection={collection}
                                selectedCategory={selectedCategory}
                                selectedJewelrySubCategories={selectedJewelrySubCategories}
                                showJewelrySubCategories={showJewelrySubCategories}
                                onCategoryChange={setSelectedCategory}
                                onSubCategoryChange={setSelectedJewelrySubCategories}
                                onToggleExpansion={setShowJewelrySubCategories}
                                onPageChange={setCurrentPage}
                              />
                            ) : (
                              <label key={collection.handle} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="collection"
                                  checked={selectedCategory === collection.handle}
                                  onChange={() => {
                                    setSelectedCategory(collection.handle);
                                    setCurrentPage(1);
                                  }}
                                  className="w-4 h-4 accent-primary bg-background border-border focus:ring-primary focus:ring-2"
                                />
                                <span className="text-sm text-muted-foreground/50">{collection.title}</span>
                              </label>
                             )
                         )}
                  </div>
                </div>

                {/* Price Range */}
                <div className="mb-6 w-full">
                  <label className="text-sm font-medium mb-3 block">
                    Price Range: ${priceRange[0]} - ${priceRange[1]}
                  </label>
                  <div className="w-full">
                    <input
                      type="range"
                      min="0"
                      max="500"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-none"
                    />
                  </div>
                  <div className="w-full flex justify-between text-xs text-muted-foreground mt-1">
                    <span>$0</span>
                    <span>$500</span>
                  </div>
                </div>

                 {/* Sizes */}
                 <div className="mb-6">
                   <label className="text-sm font-medium mb-3 block">Clothing Size Ranges</label>
                   <div className="grid grid-cols-3 gap-2">
                     {getClothingSizeGroups().map((sizeGroup) => (
                       <button
                         key={sizeGroup.label}
                         onClick={() => {
                           if (selectedSizes.includes(sizeGroup.label)) {
                             setSelectedSizes(selectedSizes.filter(s => s !== sizeGroup.label));
                           } else {
                             setSelectedSizes([...selectedSizes, sizeGroup.label]);
                           }
                         }}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-all w-full flex items-center justify-center ${
                            selectedSizes.includes(sizeGroup.label)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-foreground border-border hover:border-primary/50'
                          }`}
                       >
                         {sizeGroup.displayLabel}
                       </button>
                     ))}
                   </div>
                 </div>

                 {/* Colors */}
                 <div className="mb-6">
                   <label className="text-sm font-medium mb-3 block">Colors</label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { name: 'Black', color: '#000000' },
                        { name: 'White', color: '#FFFFFF' },
                        { name: 'Brown', color: '#8B4513' },
                        { name: 'Beige', color: '#F5F5DC' },
                        { name: 'Sage', color: '#9CAF88' },
                        { name: 'Blue', color: '#4169E1' },
                        { name: 'Pink', color: '#FFB6C1' },
                        { name: 'Gold', color: '#B8860B' }
                      ].map(color => {
                        const isSelected = selectedColors.includes(color.name.toLowerCase());
                        const needsLightOutline = ['White', 'Beige'].includes(color.name);
                        const outlineColor = needsLightOutline ? '#D2B48C' : '#DEB887';
                        
                        return (
                          <button
                            key={color.name}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedColors(selectedColors.filter(c => c !== color.name.toLowerCase()));
                              } else {
                                setSelectedColors([...selectedColors, color.name.toLowerCase()]);
                              }
                            }}
                            className="p-0"
                            title={color.name}
                          >
                            <div
                              className={`w-8 h-8 rounded-full border-2 transition-all ${isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:scale-110'}`}
                              style={{ 
                                backgroundColor: color.color,
                                borderColor: outlineColor
                              }}
                            />
                          </button>
                        );
                      })}
                   </div>
                 </div>

                  {/* Sort */}
                  <div className="mb-6">
                   <label className="text-sm font-medium mb-3 block">Sort By</label>
                   <div className="space-y-2">
                      {[
                        { value: "updated", label: "Recently Updated" },
                        { value: "newest", label: "Season's Freshest" },
                        { value: "oldest", label: "Timeless Pieces" },
                        { value: "price_asc", label: "Price: Low to High" },
                        { value: "price_desc", label: "Price: High to Low" }
                      ].map(option => (
                        <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="sortBy"
                            checked={sortBy === option.value}
                            onChange={() => setSortBy(option.value as 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated')}
                           className="w-4 h-4 accent-primary bg-background border-border focus:ring-primary focus:ring-2"
                         />
                         <span className="text-sm">{option.label}</span>
                       </label>
                     ))}
                  </div>
                </div>

                 {/* Hide Out of Stock Filter */}
                 <div className="mb-6">
                   <label className="text-sm font-medium mb-3 block">Stock Filter</label>
                   <div className="flex items-center space-x-2">
                     <Checkbox
                       id="hide-out-of-stock"
                       checked={hideOutOfStock}
                       onCheckedChange={(checked) => {
                         const isChecked = checked === true;
                         setHideOutOfStock(isChecked);
                       }}
                     />
                     <Label htmlFor="hide-out-of-stock" className="text-sm font-normal cursor-pointer">
                       Hide out of stock products
                     </Label>
                   </div>
                 </div>

                 {/* Clear Filters */}
                 <Button onClick={clearAllFilters} variant="outline" className="w-full mt-4">
                   Clear All Filters
                 </Button>
                </div> {/* End Filter Content */}
               </div>
             </div>

                {/* Products Grid Column */}
                <div className="flex-1 min-w-0">
                     {/* Hero Banner - Toggle controlled visibility */}
                     {heroElementsVisible && (
                        <div className="mb-4 relative">
                        <div className="relative bg-gradient-to-r from-primary/20 via-accent/10 to-primary/20 rounded-lg p-6 border border-primary/30 backdrop-blur-sm overflow-hidden">
                          {/* Decorative corner flourishes */}
                          <div className="absolute top-2 left-4 w-8 h-8 opacity-30">
                             <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                             <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                             <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                          </div>
                          <div className="absolute top-2 right-4 w-8 h-8 opacity-30">
                             <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                             <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                             <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                          </div>
                          <div className="absolute bottom-2 left-4 w-8 h-8 opacity-30">
                             <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                             <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                             <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                          </div>
                          <div className="absolute bottom-2 right-4 w-8 h-8 opacity-30">
                             <div className="w-full h-full border-2 border-border/60 rounded-full"></div>
                             <div className="absolute top-1 left-1 w-6 h-6 border border-border/40 rounded-full"></div>
                             <div className="absolute top-2 left-2 w-4 h-4 border border-border/30 rounded-full"></div>
                          </div>
                          
                          {/* Depth layers */}
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 rounded-lg"></div>
                          
                          <div className="relative grid md:grid-cols-12 gap-6 items-center">
                            {/* Main Message - 8 columns */}
                            <div className="md:col-span-8">
                              <div className="flex items-baseline gap-3 mb-2">
                                <h3 className="text-2xl font-boho text-primary font-semibold" style={{WebkitTextStroke: '0.25px hsl(var(--foreground))'}}>Discover Your Perfect Boho Style</h3>
                                <Badge className="bg-gradient-to-r from-primary to-accent text-background text-xs font-medium px-2 py-1 shadow-md border border-border/30 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3 text-background drop-shadow-sm" />
                                  <span className="drop-shadow-sm">Handpicked</span>
                                </Badge>
                              </div>
                              <p className="text-sm text-foreground leading-relaxed">
                                Discover our complete collection of bohemian treasures, each piece <span className="font-semibold text-primary">lovingly selected</span> for its unique character and artisan quality.
                              </p>
                            </div>

                            {/* Value Prop - 4 columns */}
                            <div className="md:col-span-4 text-center md:text-right">
                              <div className="relative bg-gradient-to-br from-foreground/30 to-primary/25 backdrop-blur-sm rounded-xl p-4 border-2 border-primary/40 shadow-xl">
                                {/* Decorative inner border */}
                                <div className="absolute inset-2 border border-primary/20 rounded-lg pointer-events-none"></div>
                                
                                <div className="text-2xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent mb-1" style={{WebkitTextStroke: '0.25px hsl(var(--foreground))'}}>
                                  Free Spirit
                                </div>
                                <div className="text-xs text-foreground font-medium mb-2 drop-shadow-sm">Express your unique style</div>
                                <div className="flex items-center justify-center md:justify-end gap-2 text-xs text-background font-medium bg-accent/80 rounded-full px-2 py-1 border border-accent/40">
                                  <Star className="w-3 h-3 text-background drop-shadow-sm" />
                                  <span className="drop-shadow-sm">Authentic boho lifestyle</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
               {/* Products Anchor for Refresh/Pagination Scrolling */}
               <div id="products" className="scroll-mt-4"></div>
              
              {/* Products Grid */}
              {(productsLoading || !hasInitialLoad) ? (
                <div className="space-y-4">
                  <div className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-primary mr-3" />
                      <div>
                        <h3 className="text-xl font-semibold">Curating your selection...</h3>
                        <p className="text-muted-foreground mt-1">Finding the perfect pieces for you</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Skeleton Cards Grid */}
                  <div className={`grid ${getGridClasses()} ${getGapClass()} w-full min-w-0 overflow-hidden`}>
                    {Array.from({ length: 24 }).map((_, i) => (
                      <ProductCardSkeleton key={i} />
                    ))}
                  </div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-xl font-semibold mb-2">No products found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery ? `No results for "${searchQuery}"` : 'Try adjusting your filters to discover more pieces'}
                  </p>
                  {(searchQuery || selectedCategory || selectedSizes.length > 0 || selectedColors.length > 0 || hideOutOfStock) && (
                    <Button onClick={clearAllFilters} variant="outline">
                      Clear All Filters
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className={`grid ${getGridClasses()} ${getGapClass()} w-full min-w-0 overflow-hidden`}>
                    {filteredProducts.map((product, index) => (
                      <EnhancedShopifyProductCard
                        key={product.id}
                        product={product}
                        size={viewSize}
                        columnCount={columnCount}
                        viewSize={viewSize}
                        matchedVariant={product._matchedVariant}
                        showPurchaseControls={false}
                        onImageLoad={index < 6 ? () => setLoadedImageCount(prev => prev + 1) : undefined}
                      />
                   ))}
                  </div>


                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1 sm:gap-2 pt-8 px-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="flex-shrink-0 px-2 sm:px-3"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">Previous</span>
                      </Button>
                      
                      <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          return (
                            <Button
                              key={pageNumber}
                              variant={pageNumber === currentPage ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCurrentPage(pageNumber)}
                              className="flex-shrink-0 min-w-[2.5rem] px-2"
                            >
                              {pageNumber}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="flex-shrink-0 px-2 sm:px-3"
                      >
                        <span className="hidden sm:inline mr-1">Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  {/* Results Summary */}
                   <div className="text-center text-sm text-muted-foreground">
                     Showing {startIndex + 1}-{Math.min(endIndex, allFilteredProducts.length)} of {allFilteredProducts.length} products
                     {searchQuery && ` for "${searchQuery}"`}
                   </div>
                   </>
               )}

               {/* Recently Viewed */}
               <div className="mt-12">
                 <RecentlyViewed />
               </div>

               {/* Page Revision (bottom only) */}
               <div className="mt-8 flex justify-end items-center gap-2">
                 <RevisionBadge 
                   pageKey="products"
                   className="opacity-70 hover:opacity-100 transition-opacity"
                 />
               </div>
               </div>
             </div>
           
           <BohoFooter />
         </div>
       </div>
     </div>
    </div>
   );
 };

export default UnifiedProductsPage;